import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  LinearProgress,
  CircularProgress,
  Box,
} from "@mui/material";
import { BarChart, CheckCircle, Star, NavigateNext } from "@mui/icons-material";
import {
  StyledMainContainer,
  StyledStatsCard,
  StyledCircleChart,
  StyledChartIcon,
  StyledRightPanel,
  StyledRetosHeader,
  StyledRetoCard,
  StyledLegend,
  StyledLegendItem,
  StyledLegendDot,
  StyledFloatingButton,
} from "./styled";

import plechaInicio from "../../img/retos-dashboard.webp";

import { colors } from "../../theme/colors";

import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function RetosDashboard() {
  const navigate = useNavigate();
  const [retosData, setRetosData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRetosData = () => {
    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_BE_URL}/rtm/challenges`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) {
          throw new Error("No tienes permisos para acceder a esta información");
        }
        return res.json();
      })
      .then((data) => {
        if (data.error) {
          throw new Error(
            data.errorMessage || "Error en la respuesta del servidor",
          );
        }
        setRetosData(data);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching retos data:", err);
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRetosData();
  }, []);

  const getStatusFromState = (state) => {
    switch (state) {
      case "completed":
        return "completed";
      case "in_course":
        return "in_progress";
      case "not_started":
      default:
        return "not_started";
    }
  };

  // Función para obtener el progreso
  const getProgressFromChallenges = (
    challengesCompleted,
    totalChallenges,
    state,
  ) => {
    if (state === "completed") {
      return 100;
    }
    if (state === "not_started" || totalChallenges === 0) {
      return 0;
    }
    return Math.round((challengesCompleted / totalChallenges) * 100);
  };

  // Función para obtener las estadísticas generales
  const getGeneralStats = () => {
    if (!retosData?.result?.response?.generalPrizes) {
      return { notStarted: 0, onCourse: 0, finished: 0, total: 0 };
    }
    const stats = retosData.result.response.generalPrizes;
    const total = stats.notStarted + stats.onCourse + stats.finished;
    return { ...stats, total };
  };

  const getMonthNumber = (monthName) => {
    const monthMap = {
      Enero: 1,
      Febrero: 2,
      Marzo: 3,
      Abril: 4,
      Mayo: 5,
      Junio: 6,
      Julio: 7,
      Agosto: 8,
      Septiembre: 9,
      Octubre: 10,
      Noviembre: 11,
      Diciembre: 12,
    };
    return monthMap[monthName] || 1;
  };

  const handleRetoClick = (monthNumber) => {
    navigate(`/recompensas/${monthNumber}`);
  };

  if (loading) {
    return (
      <StyledMainContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            width: "100%",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <CircularProgress sx={{ color: colors.secondary }} />
          <Typography sx={{ color: "white" }}>Cargando retos...</Typography>
        </div>
      </StyledMainContainer>
    );
  }

  // Mostrar error
  if (error) {
    return (
      <StyledMainContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            width: "100%",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <Typography sx={{ color: "white", textAlign: "center" }}>
            Error al cargar los retos: {error}
          </Typography>
          <button
            onClick={fetchRetosData}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: colors.secondary,
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Reintentar
          </button>
        </div>
      </StyledMainContainer>
    );
  }

  const generalStats = getGeneralStats();
  const statistics = retosData?.result?.response?.statistics || [];

  return (
    <div style={{ width: "100%", backgroundColor: colors.primary }}>
      <StyledMainContainer>
        <StyledStatsCard>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary,
              fontWeight: "bold",
              textAlign: "center",
              fontSize: "18px",
              lineHeight: "1.4",
              marginBottom: "1rem",
            }}
          >
            Estadísticas generales
            <br />
            de Retos Banco Industrial
          </Typography>

          {(() => {
            const { notStarted, onCourse, finished, total } = generalStats;

            const donutData = {
              labels: ["En curso", "Cumplidos", "No iniciados"],
              datasets: [
                {
                  data: [onCourse, finished, notStarted],
                  backgroundColor: [
                    colors.primary,
                    colors.secondary,
                    colors.orange.title,
                  ],
                  borderWidth: 0,
                  hoverOffset: 4,
                },
              ],
            };

            const donutOptions = {
              cutout: "65%",
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => ` ${ctx.label}: ${ctx.parsed}`,
                  },
                },
              },
            };

            return (
              <Box
                sx={{ position: "relative", width: "180px", margin: "0 auto" }}
              >
                <Doughnut data={donutData} options={donutOptions} />
                {/* Número central */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "28px",
                      fontWeight: "bold",
                      color: colors.primary,
                    }}
                  >
                    {total}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: colors.primary,
                      lineHeight: 1,
                    }}
                  >
                    retos
                  </Typography>
                </Box>
              </Box>
            );
          })()}

          <StyledLegend>
            <StyledLegendItem>
              <StyledLegendDot color={colors.primary} />
              <span>Retos en curso</span>
            </StyledLegendItem>
            <StyledLegendItem>
              <StyledLegendDot color={colors.secondary} />
              <span>Retos cumplidos</span>
            </StyledLegendItem>
            <StyledLegendItem>
              <StyledLegendDot color={colors.orange.title} />
              <span>Retos no iniciados</span>
            </StyledLegendItem>
          </StyledLegend>
        </StyledStatsCard>

        <StyledRightPanel>
          <div style={{ position: "relative" }}>
            <StyledRetosHeader>
              <Typography
                variant="h4"
                sx={{
                  color: colors.primary,
                  fontWeight: "bold",
                  margin: 0,
                  fontSize: "28px",
                }}
              >
                Retos {new Date().getFullYear()}
              </Typography>
            </StyledRetosHeader>

            <Typography
              variant="h6"
              sx={{
                color: colors.secondary,
                marginBottom: "2rem",
                fontWeight: 400,
                width: "250px",
                fontSize: "20px",
                lineHeight: "1.5rem",
                marginLeft: "4rem",
                display: { xs: "none", md: "block" },
              }}
            >
              Tu próximo reto es tu próxima recompensa
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.secondary,
                marginBottom: "0rem",
                fontWeight: 400,
                fontSize: "24px",
                lineHeight: "1.5rem",
                textAlign: "center",
                display: { xs: "block", md: "none" },
              }}
            >
              Actividades mensuales
            </Typography>
            <Box
              component="img"
              src={plechaInicio}
              alt="Test Financiero - Banco Industrial"
              sx={{
                width: "100%",
                height: "auto",
                maxWidth: {
                  xs: "150px",
                  sm: "150px",
                  md: "150px",
                  lg: "150px",
                },
                objectFit: "contain",
                display: { xs: "none", md: "block" },
                margin: "0 auto",
                position: "absolute",
                top: "-3rem",
                right: "0px",
              }}
            />
          </div>

          <Grid container spacing={2}>
            {statistics.length > 0 ? (
              statistics.map((reto, index) => {
                const status = getStatusFromState(reto.state);
                const progress = getProgressFromChallenges(
                  reto.challengesCompleted,
                  reto.totalChallenges,
                  reto.state,
                );
                const monthNumber = getMonthNumber(reto.name);

                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <StyledRetoCard
                      month
                      completed={status === "completed"}
                      onClick={() => handleRetoClick(monthNumber)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="reto-content">
                        {status === "completed" && (
                          <CheckCircle sx={{ color: colors.green.secondary }} />
                        )}
                        {status === "in_progress" && (
                          <BarChart sx={{ color: colors.primary }} />
                        )}
                        {status === "not_started" && (
                          <Star sx={{ color: colors.primary }} />
                        )}

                        <div className="reto-text">
                          <Typography className="reto-title">
                            {reto.name}
                          </Typography>

                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                              height: "6px",
                              margin: "0px !important",
                              borderRadius: "3px",
                              backgroundColor: "#E8EFF7",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor:
                                  status === "completed"
                                    ? colors.green.secondary
                                    : status === "in_progress"
                                      ? colors.primary
                                      : colors.primary,
                              },
                              marginBottom: "0.5rem",
                            }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            marginBottom: "1rem",
                            fontWeight: "500",
                            color: colors.gray.textLight,
                          }}
                        >
                          {reto.challengesCompleted}/{reto.totalChallenges}
                        </Typography>
                        <NavigateNext className="arrow-icon" />
                      </div>
                    </StyledRetoCard>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography sx={{ color: "white", textAlign: "center" }}>
                  No hay retos disponibles
                </Typography>
              </Grid>
            )}
          </Grid>
        </StyledRightPanel>
      </StyledMainContainer>
    </div>
  );
}
